import { c as _c } from "react/compiler-runtime";
import React, { useId, useRef, useState } from "react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { TerraLink } from "../TerraLink/TerraLink";
import { Icon } from "@/components/formatters/MXPIcon";
import { renderContentfulImage } from "../../utils/renderImage";
import { copyToClipboard, renderIcon } from "../CardEcommerce/CardEcommerceMethods";
import type { PromotionBannerValue } from "./PromotionBanner";
const DEFAULT_DISMISS_LABEL = "No thanks, hide this offer";
const CARD_IMAGE_WIDTH = 360;
const SWIPE_THRESHOLD_PX = 30;
const cardImageHeight = (image: NonNullable<PromotionBannerValue["cardImage"]>) => image.width && image.height ? Math.round(CARD_IMAGE_WIDTH * image.height / image.width) : Math.round(CARD_IMAGE_WIDTH * 9 / 16);
interface PromoCardProps {
  value: PromotionBannerValue;
  containerRef?: React.Ref<HTMLDivElement>;
}
export const PromoCard = t0 => {
  const $ = _c(22);
  const {
    value,
    containerRef
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const cardId = useId();
  const isDarkGray = value?.theme === "Dark Gray";
  const teaser = value?.teaser;
  const pillRef = useRef(null);
  const swipeStartY = useRef(null);
  const didSwipe = useRef(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (didSwipe.current) {
        didSwipe.current = false;
        return;
      }
      setIsOpen(_temp);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const toggle = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsOpen(false);
      pillRef.current?.focus();
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const minimize = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e => {
      swipeStartY.current = e.clientY;
      didSwipe.current = false;
      e.currentTarget.setPointerCapture(e.pointerId);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const onPillPointerDown = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e_0 => {
      if (swipeStartY.current === null) {
        return;
      }
      const deltaY = e_0.clientY - swipeStartY.current;
      swipeStartY.current = null;
      if (Math.abs(deltaY) < SWIPE_THRESHOLD_PX) {
        return;
      }
      didSwipe.current = true;
      setIsOpen(deltaY < 0);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const onPillPointerUp = t4;
  const imagePopulated = value?.cardImage?.url;
  const t5 = value?.contentfulId;
  let t6;
  if ($[4] !== cardId || $[5] !== copied || $[6] !== imagePopulated || $[7] !== isDarkGray || $[8] !== isOpen || $[9] !== value) {
    t6 = isOpen && <div id={cardId} className="overflow-y-auto rounded-lg shadow-card z-10 bottom-0 left-0 text-gray-m10 absolute shadow-md max-sm:right-0 max-sm:max-h-[85dvh] max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:px-5 "><button type="button" aria-label="Minimize offer" aria-controls={cardId} className={`absolute right-8 sm:right-3 top-3 z-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-gray-m10 shadow-card [&_.t2-icon]:pointer-events-none [&_.t2-icon]:h-4 [&_.t2-icon_svg_path]:fill-gray-m10 ${imagePopulated ? "border border-gray-m10 bg-white" : "bg-gray-m2"}`} onClick={minimize}><span aria-hidden="true" className="flex"><Icon type="t2u-chevron-right" size={10} rotateDeg="90" /></span></button>{imagePopulated && <div className="max-h-[150px] w-auto">{value?.cardImage?.url && renderContentfulImage(value?.cardImage?.url, value?.cardImage?.altText, CARD_IMAGE_WIDTH, cardImageHeight(value?.cardImage), "block h-auto w-full object-cover max-h-[150px] max-sm:rounded-t-2xl", undefined, value?.cardImage?.focusArea, "low", false, `(max-width: 639px) 100vw, ${CARD_IMAGE_WIDTH}px`)}</div>}<div className="flex flex-col gap-4 p-5 [&_p]:mb-0 bg-white">{value?.header && <div className={`t2-promo-card__header line-clamp-3 text-lg font-bold leading-[1.3] ${imagePopulated ? "" : "pr-8"}`}>{value.header}</div>}{(value?.details || value?.detailsJson) && <div className="text-base leading-[1.3] line-clamp-3">{value?.detailsJson ? documentToReactComponents(value.detailsJson) : value?.details}</div>}{value?.promoCode && <div className="flex justify-start items-center gap-2"><button type="button" className="inline-flex h-9 w-fit max-w-full cursor-copy items-center gap-2 rounded-lg border-0 px-2 py-1 text-xs font-extrabold uppercase tracking-[0.24px] bg-green-m2 text-green-m15" aria-label={copied ? `Promo code ${value.promoCode} copied` : `Copy promo code ${value.promoCode}`} onClick={() => value.promoCode && copyToClipboard(value.promoCode, setCopied)}>{value?.promoCodeLabel}<span className={"whitespace-nowrap rounded-lg px-1.5 py-0.5 text-xs\n                    bg-green-m15 text-green-m2"}>{value.promoCode}</span></button><span className="inline-flex shrink-0 items-center">{renderIcon(copied, isDarkGray ? "dark" : "normal")}</span></div>}<div>{value?.cta && <TerraLink primary={value.cta.primary ?? false} destination={value.cta.destination} label={value.cta.label} excludeDefaultClasses={true} className={`flex items-center justify-center bg-yellow-m10 cursor-pointer rounded-full w-full px-9 py-2 text-center text-lg font-bold uppercase leading-5 tracking-[1.44px] shadow-none transition-[color,background-color,border-color,opacity] 
                    duration-150 ease-in-out [text-decoration:none]! max-sm:px-[34px] max-sm:py-1.5 max-sm:text-base !text-gray-m9 h-[40px]
                    ${isDarkGray ? "border-yellow-m10 bg-yellow-m10 !text-gray-m10 hover:bg-yellow-m10-hover" : "border-gray-m10 bg-gray-m10 text-white hover:border-gray-m9 hover:bg-yellow-m10-hover"}`} />}<button type="button" className="w-full cursor-pointer text-sm text-blue-trmb rounded-full underline hover:no-underline bg-white py-3 pb-0 px-4" onClick={e_1 => value?.handleClose?.(e_1)}>{value?.dismissLabel || DEFAULT_DISMISS_LABEL}</button></div>{value?.finePrintJson && <div className="text-xs leading-[1.4] [&_a]:text-blue-trmb [&_a]:underline line-clamp-3">{documentToReactComponents(value.finePrintJson)}</div>}</div></div>;
    $[4] = cardId;
    $[5] = copied;
    $[6] = imagePopulated;
    $[7] = isDarkGray;
    $[8] = isOpen;
    $[9] = value;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== cardId || $[12] !== isDarkGray || $[13] !== isOpen || $[14] !== teaser || $[15] !== value) {
    t7 = teaser && <><div className="w-full flex items-center gap-2 md:max-w-[350px]"><button ref={pillRef} type="button" className={`max-sm:rounded-t-lg w-full flex flex-row justify-center items-center min-w-0 rounded-full cursor-pointer font-bold text-center uppercase border-2 shadow-card 
                transition-[color,background-color,border-color] duration-150 ease-in-out 
                text-base tracking-[1.44px] leading-5 py-2 px-3 sm:py-2 sm:px-2 sm:pl-9 shadow-md
                relative max-sm:touch-none max-sm:rounded-none max-sm:border-0 max-sm:pt-6 max-sm:pb-4 max-sm:shadow-none
                max-sm:pb-12 line-clamp-2 
                ${!isDarkGray ? "bg-yellow-m10 hover:bg-yellow-m10-hover border-yellow-m10 hover:border-yellow-m10-hover text-gray-m10" : "bg-gray-m9 border-gray-m10 hover:bg-gray-m10 text-white"}`} aria-expanded={isOpen} aria-controls={cardId} onClick={toggle} onPointerDown={onPillPointerDown} onPointerUp={onPillPointerUp} onPointerCancel={() => {
          swipeStartY.current = null;
        }}><span aria-hidden="true" className="absolute left-1/2 top-2 hidden h-1 w-10 -translate-x-1/2 rounded-full bg-current opacity-40 max-sm:block" /><span className="line-clamp-2">{teaser}</span><span aria-hidden="true" className="max-sm:hidden ml-2 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-white text-gray-m10 shadow-card [&_.t2-icon]:pointer-events-none [&_.t2-icon]:h-4 [&_.t2-icon_svg_path]:fill-gray-m10"><Icon type="t2u-chevron-right" size={10} rotateDeg={isOpen ? "90" : "270"} className="transition-transform duration-150" /></span></button></div><button type="button" className={`w-full cursor-pointer text-sm text-blue-trmb rounded-full underline hover:no-underline bg-white md:bg-white/50 hover:bg-white/75 py-3 px-4 shadow-md max-sm:rounded-b-none max-sm:rounded-t-lg max-sm:shadow-none max-sm:-mt-8 ${isOpen ? "max-sm:z-1" : "max-sm:z-10"}`} onClick={e_2 => value?.handleClose?.(e_2)}>{value?.dismissLabel || DEFAULT_DISMISS_LABEL}</button></>;
    $[11] = cardId;
    $[12] = isDarkGray;
    $[13] = isOpen;
    $[14] = teaser;
    $[15] = value;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== containerRef || $[18] !== t5 || $[19] !== t6 || $[20] !== t7) {
    t8 = <div ref={containerRef} className="t2-promotion-banner t2-promotion-banner--promo-card fixed bottom-6 left-24 z-[25] flex flex-col items-end gap-3 w-full sm:max-w-[350px] max-sm:inset-x-0 max-sm:bottom-0 max-sm:gap-0 max-sm:shadow-md max-sm:px-5" key={t5}>{t6}{t7}</div>;
    $[17] = containerRef;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
};
function _temp(open) {
  return !open;
}